import React, { useEffect, useState } from 'react';
import { useVendorAuth } from '../../contexts/VendorAuthContext';
import { BarChart3, Download } from 'lucide-react';

const fmt = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—';

export default function VendorLedger() {
  const { vendorFetch } = useVendorAuth();
  const [ledger, setLedger] = useState(null);
  const [frm, setFrm] = useState('');
  const [to, setTo] = useState('');

  const load = () => {
    const params = new URLSearchParams();
    if (frm) params.set('frm', frm);
    if (to) params.set('to', to);
    vendorFetch(`/partner/ledger${params.toString() ? '?' + params : ''}`)
      .then(setLedger).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="vendor-ledger-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-bold text-xl text-slate-900">Ledger</h1>
          <p className="text-sm text-slate-500 mt-0.5">POs issued, payments received, TDS deducted, outstanding.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="date" value={frm} onChange={e => setFrm(e.target.value)}
            className="border border-slate-300 rounded-sm px-2 py-1.5 text-xs focus:outline-none focus:border-[#3b4872]" />
          <span className="text-slate-400 text-xs">to</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            className="border border-slate-300 rounded-sm px-2 py-1.5 text-xs focus:outline-none focus:border-[#3b4872]" />
          <button onClick={load}
            className="px-3 py-1.5 bg-[#3b4872] text-white rounded-sm text-xs font-bold hover:bg-[#2d3856]">Apply</button>
        </div>
      </div>

      {/* Summary cards */}
      {ledger && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total PO Value', value: fmt(ledger.summary?.total_po_value), sub: 'Issued to you' },
              { label: 'Paid', value: fmt(ledger.summary?.total_paid), sub: 'Received' },
              { label: 'TDS Deducted', value: fmt(ledger.summary?.total_tds_deducted), sub: 'Withheld' },
              { label: 'Outstanding', value: fmt(ledger.summary?.outstanding), sub: 'Pending payment', highlight: true },
            ].map(s => (
              <div key={s.label} className={`bg-white border rounded-sm p-4 ${s.highlight ? 'border-[#3b4872]/30' : 'border-slate-200'}`}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{s.label}</div>
                <div className={`text-xl font-extrabold mt-1 ${s.highlight ? 'text-[#3b4872]' : 'text-slate-900'}`}>{s.value}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Transactions table */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full dense-table text-xs">
              <thead><tr><th>Date</th><th>Type</th><th>Reference</th><th className="text-right">Amount</th><th>Direction</th></tr></thead>
              <tbody>
                {(ledger.transactions || []).length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-8 text-slate-400">No transactions in this period</td></tr>
                ) : (ledger.transactions || []).map((tx, i) => (
                  <tr key={i}>
                    <td>{tx.date || '—'}</td>
                    <td><span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-sm ${tx.type === 'Payment' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{tx.type}</span></td>
                    <td className="font-mono text-slate-500">{tx.ref || '—'}</td>
                    <td className="text-right tabular-nums font-semibold">{fmt(tx.amount)}</td>
                    <td>
                      {tx.direction === 'received'
                        ? <span className="text-emerald-600 font-bold text-[10px]">Received</span>
                        : <span className="text-slate-500 text-[10px]">PO Issued</span>}
                      {tx.tds_deducted > 0 && <span className="ml-1 text-[10px] text-slate-400">TDS {fmt(tx.tds_deducted)}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
